import React, { useReducer, useCallback } from "react";
import { PropTypes } from "prop-types";
import {
  useGroupTypes,
  useWorkspaceTagConfig,
} from "src/contexts/global/WorkspaceContext";
import useOrgChartDataLoader from "../hooks/useOrgChartDataLoader";
import { getDirectTeamCollectionId } from "../hooks/useOrgChartNodesAndEdges/utils";
import useUpdateTeamStoreListener from "../hooks/useUpdateTeamStoreListener";
import useUpdateMemberListener from "../hooks/useUpdateMemberListener";
import useCurrentViewTeam from "../hooks/useCurrentViewTeam";
import OrgChartContext from "./OrgChartContext";
import { orgChartReducer, getInitialOrgChartState } from "./reducer";
import { ACTION_TYPES } from "./actionTypes";

const OrgChartContextProvider = ({ teamId, children }) => {
  const tagConfig = useWorkspaceTagConfig();
  const groupTypes = useGroupTypes();

  const [state, dispatch] = useReducer(
    orgChartReducer,
    getInitialOrgChartState({ tagConfig })
  );

  const toggleExpandedNode = useCallback(
    (targetTeamId, shouldToggleExpanded) => {
      if (shouldToggleExpanded && state.expandedNodes.includes(targetTeamId)) {
        return;
      }

      const isRemoving = state.expandedNodes.includes(targetTeamId);
      let expandedNodes = [...state.expandedNodes, targetTeamId];

      const getChildTeamsRecusively = (id) => {
        const node = state.loadedTeams[id];
        if (!node) {
          return [];
        }

        return node.childTeams
          .map((child) => [child.id, ...getChildTeamsRecusively(child.id)])
          .flat();
      };

      if (isRemoving) {
        const childTeamIds = getChildTeamsRecusively(targetTeamId);

        const nodesToCollapse = [...childTeamIds, targetTeamId]
          .map((id) => [id, getDirectTeamCollectionId(id)])
          .flat();

        expandedNodes = state.expandedNodes.filter(
          (id) => !nodesToCollapse.includes(id)
        );
      }

      dispatch({
        type: ACTION_TYPES.UPDATE_EXPANDED_TEAMS,
        payload: {
          expandedNodes,
        },
      });
    },
    [state, dispatch]
  );

  const { loadChildTeamsById } = useOrgChartDataLoader({
    dispatch,
    state,
    toggleExpandedNode,
  });

  const mergePartialData = useCallback(
    (payload) => dispatch({ type: ACTION_TYPES.MERGE_PARTIAL_DATA, payload }),
    [dispatch]
  );

  useCurrentViewTeam({ loadChildTeamsById, dispatch, state, teamId });
  useUpdateMemberListener({ mergePartialData });
  useUpdateTeamStoreListener({ state, mergePartialData });

  return (
    <OrgChartContext.Provider
      value={{
        groupTypes,
        state,
        dispatch,
        loadChildTeamsById,
        toggleExpandedNode,
        mergePartialData,
        teamId,
      }}
    >
      {children}
    </OrgChartContext.Provider>
  );
};

OrgChartContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  teamId: PropTypes.string,
};

export default OrgChartContextProvider;
